import { REGISTRY_BLOCK } from '@web-studio/shared-schemas';
import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import type { BlockRenderProps } from '../../types';

const MEMBER_COUNT = 4;

type TeamMember = {
  index: number;
  name: string;
  role: string;
  bio: string;
  image?: string;
};

function kitchenTeamModifier(registryBlockKey?: string): string {
  if (registryBlockKey === REGISTRY_BLOCK.presentation.kitchenTeamRow) {
    return 'ws-kitchen-team--row';
  }
  if (registryBlockKey === REGISTRY_BLOCK.presentation.kitchenTeamBand) {
    return 'ws-kitchen-team--band';
  }
  return 'ws-kitchen-team--lead';
}

function readMembers(block: BlockRenderProps['block'], locale: string): TeamMember[] {
  const members: TeamMember[] = [];
  for (let index = 0; index < MEMBER_COUNT; index += 1) {
    const name = pickBlockField(block.content, `memberName${index}`, locale);
    const role = pickBlockField(block.content, `memberRole${index}`, locale);
    const bio = pickBlockField(block.content, `memberBio${index}`, locale);
    const image = pickMediaUrl(block, `memberImage${index}`);
    if (!name.trim() && !role.trim() && !bio.trim()) continue;
    members.push({ index, name, role, bio, image });
  }
  return members;
}

function MemberPhoto({
  fieldKey,
  image,
  alt,
  editable,
  target,
  className,
  sizes,
}: {
  fieldKey: string;
  image?: string;
  alt: string;
  editable?: boolean;
  target: BlockRenderProps['activeEditTarget'];
  className: string;
  sizes: string;
}) {
  if (image) {
    return (
      <EditableMedia
        fieldKey={fieldKey}
        editable={editable}
        activeEditTarget={target}
        className={className}
        src={buildSiteImageProxyUrl(image, 960, 78)}
        srcSet={buildSiteImageSrcSet(image, [320, 480, 720, 960], 78)}
        sizes={sizes}
        alt={alt}
        width={720}
        height={900}
        loading="lazy"
        decoding="async"
      />
    );
  }
  if (!editable) return null;
  const active =
    target?.kind === 'media-field' && target.fieldKey === fieldKey;
  return (
    <button
      type="button"
      className={[className, `${className}--empty`, 'ws-edit-media', active ? 'ws-edit-child--active ws-edit-media--active' : '']
        .filter(Boolean)
        .join(' ')}
      data-edit-child="media-field"
      data-field-key={fieldKey}
      aria-label={alt || 'Team photo'}
    />
  );
}

export function KitchenTeamBlock({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const members = readMembers(block, locale);
  const leadImage = pickMediaUrl(block, 'leadImage');
  const target = isActive ? activeEditTarget : null;
  const modifier = kitchenTeamModifier(block.registryBlockKey);
  const usesLeadPhoto =
    modifier === 'ws-kitchen-team--lead' || modifier === 'ws-kitchen-team--band';

  return (
    <section
      className={['ws-kitchen-team', modifier, isActive ? 'ws-block--active' : '']
        .filter(Boolean)
        .join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="team"
    >
      <div className="ws-kitchen-team__inner">
        {usesLeadPhoto ? (
          <div className="ws-kitchen-team__lead-visual">
            <MemberPhoto
              fieldKey="leadImage"
              image={leadImage}
              alt={sectionTitle || 'Kitchen team'}
              editable={editable}
              target={target}
              className="ws-kitchen-team__lead-photo"
              sizes={SITE_IMAGE_SIZES.kitchenTeamLead}
            />
          </div>
        ) : null}

        <div className="ws-kitchen-team__copy">
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-kitchen-team__title"
          >
            {sectionTitle}
          </EditableContent>
          <EditableContent
            as="p"
            fieldKey="supporting"
            editable={editable}
            activeEditTarget={target}
            className="ws-kitchen-team__supporting"
          >
            {supporting}
          </EditableContent>

          {modifier === 'ws-kitchen-team--row' ? (
            <ul className="ws-kitchen-team__row">
              {members.map((member) => (
                <li key={member.index} className="ws-kitchen-team__card">
                  <div className="ws-kitchen-team__card-photo">
                    <MemberPhoto
                      fieldKey={`memberImage${member.index}`}
                      image={member.image}
                      alt={member.name || sectionTitle || 'Team member'}
                      editable={editable}
                      target={target}
                      className="ws-kitchen-team__member-photo"
                      sizes={SITE_IMAGE_SIZES.kitchenTeamMember}
                    />
                  </div>
                  <EditableContent
                    fieldKey={`memberName${member.index}`}
                    editable={editable}
                    activeEditTarget={target}
                    className="ws-kitchen-team__member-name"
                  >
                    {member.name}
                  </EditableContent>
                  <EditableContent
                    fieldKey={`memberRole${member.index}`}
                    editable={editable}
                    activeEditTarget={target}
                    className="ws-kitchen-team__member-role"
                  >
                    {member.role}
                  </EditableContent>
                  <EditableContent
                    as="p"
                    fieldKey={`memberBio${member.index}`}
                    editable={editable}
                    activeEditTarget={target}
                    className="ws-kitchen-team__member-bio"
                  >
                    {member.bio}
                  </EditableContent>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="ws-kitchen-team__roster">
              {members.map((member) => (
                <li key={member.index} className="ws-kitchen-team__person">
                  <EditableContent
                    fieldKey={`memberName${member.index}`}
                    editable={editable}
                    activeEditTarget={target}
                    className="ws-kitchen-team__member-name"
                  >
                    {member.name}
                  </EditableContent>
                  <EditableContent
                    fieldKey={`memberRole${member.index}`}
                    editable={editable}
                    activeEditTarget={target}
                    className="ws-kitchen-team__member-role"
                  >
                    {member.role}
                  </EditableContent>
                  <EditableContent
                    as="p"
                    fieldKey={`memberBio${member.index}`}
                    editable={editable}
                    activeEditTarget={target}
                    className="ws-kitchen-team__member-bio"
                  >
                    {member.bio}
                  </EditableContent>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
